import React, { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Users,
  UserPlus,
  Edit,
  Trash2,
  Phone,
  Mail,
  MapPin,
  Calendar,
  AlertCircle,
  UserCircle2,
  Loader2,
  Key,
  KeyRound,
  UserX,
  ShieldCheck,
  Eye,
  EyeOff,
  FileText,
  AlertTriangle,
  CreditCard
} from "lucide-react";
import api from "./api";
import { cn } from "@/lib/utils";
import { formatPhoneNumber, formatPhoneOnInput, capitalizeFirst, formatCodiceFiscaleOnInput, validateCodiceFiscale } from "@/utils/helpers";

// Format date for display
const formatDate = (date) => {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("it-IT");
};

// Calculate days until expiry
const getDaysUntilExpiry = (expiryDate) => {
  if (!expiryDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(expiryDate);
  expiry.setHours(0, 0, 0, 0);
  const diffTime = expiry - today;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// Get document status (expired, expiring, valid)
const getDocumentStatus = (expiryDate) => {
  const days = getDaysUntilExpiry(expiryDate);
  if (days === null) return null;
  if (days < 0) return "expired";
  if (days <= 60) return "expiring";
  return "valid";
};

// Document types
const DOCUMENT_TYPES = [
  "Carta d'identità",
  "Patente di guida",
  "Passaporto",
  "Permesso di soggiorno",
  "Altro"
];

export default function Operatori({ operators, setOperators, onRefresh }) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingOperator, setEditingOperator] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    birthDate: "",
    birthPlace: "",
    fiscalCode: "",
    residenceAddress: "",
    residenceCity: "",
    residenceProvince: "",
    residenceCap: "",
    email: "",
    phone: "",
    username: "",
    documentType: "",
    documentNumber: "",
    documentIssuer: "",
    documentIssueDate: "",
    documentExpiryDate: "",
    // 22/07/2026 · v1.10.0 — Scadenza visita medica del lavoro
    // (D.Lgs 81/2008). Monitorata nello scadenzario del cruscotto.
    medicalVisitExpiryDate: "",
    role: "operatore_agenzia",
    status: "attivo"
  });
  const [errors, setErrors] = useState({});
  
  // Account management state
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [accountDialogMode, setAccountDialogMode] = useState("create");
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [passwordForm, setPasswordForm] = useState({ password: "", confirmPassword: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({});

  // Count operators with expiring documents
  const expiringDocsCount = useMemo(() => {
    return operators.filter(op => {
      const status = getDocumentStatus(op.documentExpiryDate);
      return status === "expired" || status === "expiring";
    }).length;
  }, [operators]);

  const validateForm = () => {
    const newErrors = {};
    if (!form.firstName.trim()) newErrors.firstName = "Campo obbligatorio";
    if (!form.lastName.trim()) newErrors.lastName = "Campo obbligatorio";
    
    // Validate Codice Fiscale with full validation
    if (form.fiscalCode) {
      const cfResult = validateCodiceFiscale(form.fiscalCode);
      if (!cfResult.valid) {
        newErrors.fiscalCode = cfResult.error;
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setForm({
      firstName: "",
      lastName: "",
      birthDate: "",
      birthPlace: "",
      fiscalCode: "",
      residenceAddress: "",
      residenceCity: "",
      residenceProvince: "",
      residenceCap: "",
      email: "",
      phone: "",
      username: "",
      documentType: "",
      documentNumber: "",
      documentIssuer: "",
      documentIssueDate: "",
      documentExpiryDate: "",
      medicalVisitExpiryDate: "",
      role: "operatore_agenzia",
      status: "attivo"
    });
    setErrors({});
    setEditingOperator(null);
    setIsDialogOpen(false);
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      // Compute residence string for backward compatibility
      const residenceParts = [
        form.residenceAddress,
        form.residenceCap,
        form.residenceCity,
        form.residenceProvince ? `(${form.residenceProvince})` : ""
      ].filter(Boolean);
      const computedResidence = residenceParts.join(", ");

      const operatorData = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        birthDate: form.birthDate,
        birthPlace: form.birthPlace.trim(),
        fiscalCode: form.fiscalCode.toUpperCase().replace(/\s/g, ""),
        residenceAddress: form.residenceAddress.trim(),
        residenceCity: form.residenceCity.trim(),
        residenceProvince: form.residenceProvince.trim().toUpperCase(),
        residenceCap: form.residenceCap.trim(),
        residence: computedResidence,
        email: form.email.trim(),
        phone: form.phone.trim(),
        documentType: form.documentType,
        documentNumber: form.documentNumber.trim(),
        documentIssuer: form.documentIssuer.trim(),
        documentIssueDate: form.documentIssueDate,
        documentExpiryDate: form.documentExpiryDate,
        medicalVisitExpiryDate: form.medicalVisitExpiryDate,
        role: form.role,
        status: form.status,
        username: form.username.trim().toLowerCase()
      };

      if (editingOperator) {
        const updated = await api.updateOperator(editingOperator.id, operatorData);
        setOperators(prev => prev.map(op => op.id === editingOperator.id ? updated : op));
      } else {
        const created = await api.createOperator(operatorData);
        setOperators(prev => [created, ...prev]);
      }

      resetForm();
    } catch (error) {
      alert(error.message || "Errore durante il salvataggio");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (operator) => {
    setEditingOperator(operator);
    setForm({
      firstName: operator.firstName || "",
      lastName: operator.lastName || "",
      birthDate: operator.birthDate || "",
      birthPlace: operator.birthPlace || "",
      fiscalCode: operator.fiscalCode || "",
      residenceAddress: operator.residenceAddress || "",
      residenceCity: operator.residenceCity || "",
      residenceProvince: operator.residenceProvince || "",
      residenceCap: operator.residenceCap || "",
      email: operator.email || "",
      phone: operator.phone || "",
      username: operator.username || "",
      documentType: operator.documentType || "",
      documentNumber: operator.documentNumber || "",
      documentIssuer: operator.documentIssuer || "",
      documentIssueDate: operator.documentIssueDate || "",
      documentExpiryDate: operator.documentExpiryDate || "",
      medicalVisitExpiryDate: operator.medicalVisitExpiryDate || "",
      role: operator.role || "operatore",
      status: operator.status || "attivo"
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (operatorId) => {
    const operator = operators.find(op => op.id === operatorId);
    
    if (operator?.hasAccount) {
      alert("Questo operatore ha un account di accesso attivo.\n\nPer eliminare l'operatore, devi prima eliminare il suo account.");
      return;
    }
    
    if (!window.confirm("Sei sicuro di voler eliminare questo operatore?\n\nLe pratiche assegnate a questo operatore verranno aggiornate.")) {
      return;
    }

    setLoading(true);
    try {
      const result = await api.deleteOperator(operatorId);
      setOperators(prev => prev.filter(op => op.id !== operatorId));
      
      if (result.updatedCases > 0) {
        alert(`Operatore eliminato.\n${result.updatedCases} pratiche sono state aggiornate.`);
        if (onRefresh) onRefresh();
      }
    } catch (error) {
      alert(error.message || "Errore durante l'eliminazione");
    } finally {
      setLoading(false);
    }
  };

  // Account management functions
  const openAccountDialog = (operator, mode) => {
    setSelectedOperator(operator);
    setAccountDialogMode(mode);
    setPasswordForm({ password: "", confirmPassword: "" });
    setPasswordErrors({});
    setShowPassword(false);
    setAccountDialogOpen(true);
  };

  const closeAccountDialog = () => {
    setAccountDialogOpen(false);
    setSelectedOperator(null);
    setPasswordForm({ password: "", confirmPassword: "" });
    setPasswordErrors({});
  };

  const validatePassword = () => {
    const errors = {};
    if (!passwordForm.password) {
      errors.password = "La password è obbligatoria";
    } else if (passwordForm.password.length < 6) {
      errors.password = "La password deve avere almeno 6 caratteri";
    }
    if (passwordForm.password !== passwordForm.confirmPassword) {
      errors.confirmPassword = "Le password non corrispondono";
    }
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateAccount = async () => {
    if (!validatePassword()) return;
    
    setLoading(true);
    try {
      await api.createOperatorAccount(selectedOperator.id, passwordForm.password, passwordForm.username);
      const accessInfo = passwordForm.username 
        ? `Username: ${passwordForm.username}\nEmail: ${selectedOperator.email}`
        : `Email: ${selectedOperator.email}`;
      alert(`Account creato con successo!\n\n${accessInfo}\nL'operatore può ora accedere con queste credenziali.`);
      closeAccountDialog();
      // Refresh operator list to update hasAccount status
      try { const ops = await api.getOperators(); setOperators(ops); } catch {}
    } catch (error) {
      alert(error.message || "Errore durante la creazione dell'account");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!validatePassword()) return;
    
    setLoading(true);
    try {
      await api.resetOperatorPassword(selectedOperator.id, passwordForm.password);
      alert(`Password aggiornata con successo!\n\nL'operatore ${selectedOperator.fullName} può ora accedere con la nuova password.`);
      closeAccountDialog();
    } catch (error) {
      alert(error.message || "Errore durante il reset della password");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm(`Sei sicuro di voler eliminare l'account di accesso per ${selectedOperator.fullName}?\n\nL'operatore non potrà più accedere al sistema.`)) {
      return;
    }
    
    setLoading(true);
    try {
      await api.deleteOperatorAccount(selectedOperator.id);
      alert(`Account eliminato con successo.\n\n${selectedOperator.fullName} non può più accedere al sistema.`);
      closeAccountDialog();
      // Refresh operator list to update hasAccount status
      try { const ops = await api.getOperators(); setOperators(ops); } catch {}
    } catch (error) {
      alert(error.message || "Errore durante l'eliminazione dell'account");
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    // Apply auto-capitalization to text fields (not email, codes, etc.)
    const textFields = ["firstName", "lastName", "birthPlace", "residenceAddress", "residenceCity", "documentIssuer"];
    let processedValue = value;
    
    if (textFields.includes(field) && value) {
      processedValue = capitalizeFirst(value);
    }
    
    // Format phone number
    if (field === "phone" && value) {
      processedValue = formatPhoneOnInput(value);
    }
    
    // Format Codice Fiscale
    if (field === "fiscalCode" && value) {
      processedValue = formatCodiceFiscaleOnInput(value);
    }
    
    setForm(prev => ({ ...prev, [field]: processedValue }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };
  
  // Validate CF on blur
  const handleBlur = (field) => {
    if (field === "fiscalCode" && form.fiscalCode) {
      const result = validateCodiceFiscale(form.fiscalCode);
      if (!result.valid) {
        setErrors(prev => ({ ...prev, fiscalCode: result.error }));
      }
    }
  };

  // Render document expiry badge
  const renderDocumentBadge = (operator) => {
    const status = getDocumentStatus(operator.documentExpiryDate);
    if (!status || !operator.documentType) return null;
    
    const days = getDaysUntilExpiry(operator.documentExpiryDate);
    
    if (status === "expired") {
      return (
        <Badge className="text-xs bg-red-100 text-red-700 hover:bg-red-100">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Doc. scaduto
        </Badge>
      );
    }
    if (status === "expiring") {
      return (
        <Badge className="text-xs bg-amber-100 text-amber-700 hover:bg-amber-100">
          <AlertCircle className="h-3 w-3 mr-1" />
          Scade tra {days}g
        </Badge>
      );
    }
    return null;
  };

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <Card className="shadow-md">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-indigo-600" />
                  Gestione Operatori
                </CardTitle>
                <CardDescription className="flex items-center gap-3 mt-1">
                  <span>Anagrafica operatori dello studio</span>
                  {expiringDocsCount > 0 && (
                    <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      {expiringDocsCount} documenti in scadenza
                    </Badge>
                  )}
                </CardDescription>
              </div>
              <Button
                onClick={() => {
                  resetForm();
                  setIsDialogOpen(true);
                }}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="new-operator-button"
                disabled={loading}
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Nuovo Operatore
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {operators.length === 0 ? (
              <div className="text-center py-12">
                <UserCircle2 className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Nessun operatore registrato</p>
                <p className="text-sm text-slate-400 mt-1">
                  Aggiungi il primo operatore per iniziare
                </p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {operators.map((operator) => {
                  const docStatus = getDocumentStatus(operator.documentExpiryDate);
                  
                  return (
                    <Card
                      key={operator.id}
                      className={cn(
                        "hover:shadow-lg transition-shadow border-l-4",
                        operator.status === "sospeso" ? "border-l-slate-400 opacity-60" :
                        docStatus === "expired" ? "border-l-red-500" :
                        docStatus === "expiring" ? "border-l-amber-500" :
                        "border-l-indigo-400"
                      )}
                      data-testid={`operator-card-${operator.id}`}
                    >
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className={cn(
                              "h-12 w-12 rounded-full flex items-center justify-center",
                              operator.status === "sospeso" ? "bg-slate-200" : "bg-indigo-100"
                            )}>
                              <UserCircle2 className={cn(
                                "h-6 w-6",
                                operator.status === "sospeso" ? "text-slate-500" : "text-indigo-600"
                              )} />
                            </div>
                            <div>
                              <h3 className="font-semibold text-base flex items-center gap-2">
                                {operator.fullName}
                                {operator.status === "sospeso" && (
                                  <Badge variant="outline" className="text-xs text-slate-500 border-slate-400">
                                    Sospeso
                                  </Badge>
                                )}
                              </h3>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {/* Role Badge */}
                                {operator.role === "amministratore" ? (
                                  <Badge className="text-xs bg-blue-100 text-blue-700 hover:bg-blue-100">
                                    <ShieldCheck className="h-3 w-3 mr-1" />
                                    Amministratore
                                  </Badge>
                                ) : operator.role === "responsabile" ? (
                                  <Badge className="text-xs bg-purple-100 text-purple-700 hover:bg-purple-100">
                                    <ShieldCheck className="h-3 w-3 mr-1" />
                                    Responsabile
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="text-xs text-slate-500">
                                    <Users className="h-3 w-3 mr-1" />
                                    Operatore
                                  </Badge>
                                )}
                                {operator.hasAccount ? (
                                  <Badge className="text-xs bg-green-100 text-green-700 hover:bg-green-100">
                                    Account
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="text-xs text-slate-400">
                                    No account
                                  </Badge>
                                )}
                                {renderDocumentBadge(operator)}
                              </div>
                            </div>
                          </div>
                        </div>

                        <Separator className="my-3" />

                        <div className="space-y-1.5 text-sm">
                          {operator.fiscalCode && (
                            <div className="flex items-center gap-2">
                              <CreditCard className="h-4 w-4 text-slate-400" />
                              <span className="text-slate-600 font-mono text-xs">
                                {operator.fiscalCode}
                              </span>
                            </div>
                          )}
                          {operator.birthDate && (
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-slate-400" />
                              <span className="text-slate-600 text-xs">
                                {formatDate(operator.birthDate)}
                                {operator.birthPlace && ` - ${operator.birthPlace}`}
                              </span>
                            </div>
                          )}
                          {(operator.residenceCity || operator.residence) && (
                            <div className="flex items-start gap-2">
                              <MapPin className="h-4 w-4 text-slate-400 mt-0.5" />
                              <span className="text-slate-600 text-xs">
                                {operator.residenceCity 
                                  ? `${operator.residenceAddress || ""}, ${operator.residenceCap || ""} ${operator.residenceCity} ${operator.residenceProvince ? `(${operator.residenceProvince})` : ""}`
                                  : operator.residence
                                }
                              </span>
                            </div>
                          )}
                          {operator.email && (
                            <div className="flex items-center gap-2">
                              <Mail className="h-4 w-4 text-slate-400" />
                              <span className="text-slate-600 break-all text-xs">
                                {operator.email}
                              </span>
                            </div>
                          )}
                          {operator.username && (
                            <div className="flex items-center gap-2">
                              <Key className="h-4 w-4 text-blue-500" />
                              <span className="text-blue-700 text-xs font-mono font-semibold">
                                {operator.username}
                              </span>
                            </div>
                          )}
                          {operator.phone && (
                            <div className="flex items-center gap-2">
                              <Phone className="h-4 w-4 text-slate-400" />
                              <span className="text-slate-600 text-xs">{formatPhoneNumber(operator.phone)}</span>
                            </div>
                          )}
                          {operator.documentType && (
                            <div className={cn(
                              "flex items-center gap-2 p-1.5 rounded -mx-1.5",
                              docStatus === "expired" && "bg-red-50",
                              docStatus === "expiring" && "bg-amber-50"
                            )}>
                              <FileText className={cn(
                                "h-4 w-4",
                                docStatus === "expired" ? "text-red-500" :
                                docStatus === "expiring" ? "text-amber-500" :
                                "text-slate-400"
                              )} />
                              <span className={cn(
                                "text-xs",
                                docStatus === "expired" ? "text-red-700" :
                                docStatus === "expiring" ? "text-amber-700" :
                                "text-slate-600"
                              )}>
                                {operator.documentType} • scad. {formatDate(operator.documentExpiryDate)}
                              </span>
                            </div>
                          )}
                        </div>

                        <Separator className="my-3" />

                        {/* Account Management Buttons */}
                        <div className="flex flex-wrap gap-2 mb-3">
                          {!operator.hasAccount && operator.email && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-600 hover:text-green-700 hover:bg-green-50 flex-1"
                              onClick={() => openAccountDialog(operator, "create")}
                              data-testid={`create-account-${operator.id}`}
                              disabled={loading}
                            >
                              <Key className="h-4 w-4 mr-1" />
                              Crea Account
                            </Button>
                          )}
                          {operator.hasAccount && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 flex-1"
                                onClick={() => openAccountDialog(operator, "reset")}
                                data-testid={`reset-password-${operator.id}`}
                                disabled={loading}
                              >
                                <KeyRound className="h-4 w-4 mr-1" />
                                Reset Password
                              </Button>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => openAccountDialog(operator, "delete")}
                                    data-testid={`delete-account-${operator.id}`}
                                    disabled={loading}
                                  >
                                    <UserX className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Elimina account</p>
                                </TooltipContent>
                              </Tooltip>
                            </>
                          )}
                          {!operator.email && !operator.hasAccount && (
                            <p className="text-xs text-slate-400 italic w-full">
                              Aggiungi un'email per abilitare l'account
                            </p>
                          )}
                        </div>

                        {/* Edit / Delete Buttons */}
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1"
                            onClick={() => handleEdit(operator)}
                            data-testid={`edit-operator-${operator.id}`}
                            disabled={loading}
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Modifica
                          </Button>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDelete(operator.id)}
                                data-testid={`delete-operator-${operator.id}`}
                                disabled={loading || operator.hasAccount}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{operator.hasAccount ? "Elimina prima l'account" : "Elimina operatore"}</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dialog for Create/Edit Operator */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingOperator ? "Modifica Operatore" : "Nuovo Operatore"}
              </DialogTitle>
            </DialogHeader>
            
            {/* Personal Data Section */}
            <div className="space-y-4">
              <h4 className="font-medium text-sm text-slate-700 flex items-center gap-2">
                <UserCircle2 className="h-4 w-4" />
                Dati Personali
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Role and Status */}
                <div>
                  <Label htmlFor="role">Ruolo *</Label>
                  <Select
                    value={form.role}
                    onValueChange={(value) => updateField("role", value)}
                    disabled={loading}
                  >
                    <SelectTrigger data-testid="operator-role-select">
                      <SelectValue placeholder="Seleziona ruolo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="amministratore">
                        <span className="flex items-center gap-2">
                          <ShieldCheck className="h-4 w-4 text-blue-600" />
                          Amministratore
                        </span>
                      </SelectItem>
                      <SelectItem value="responsabile">
                        <span className="flex items-center gap-2">
                          <ShieldCheck className="h-4 w-4 text-purple-600" />
                          Responsabile
                        </span>
                      </SelectItem>
                      <SelectItem value="operatore_studio">
                        <span className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-blue-500" />
                          Operatore Studio
                        </span>
                      </SelectItem>
                      <SelectItem value="operatore_agenzia">
                        <span className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-orange-500" />
                          Operatore Agenzia
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="status">Stato</Label>
                  <Select
                    value={form.status}
                    onValueChange={(value) => updateField("status", value)}
                    disabled={loading}
                  >
                    <SelectTrigger data-testid="operator-status-select">
                      <SelectValue placeholder="Seleziona stato" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="attivo">
                        <span className="flex items-center gap-2">
                          <ShieldCheck className="h-4 w-4 text-green-600" />
                          Attivo
                        </span>
                      </SelectItem>
                      <SelectItem value="sospeso">
                        <span className="flex items-center gap-2">
                          <UserX className="h-4 w-4 text-red-600" />
                          Sospeso
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="firstName">Nome *</Label>
                  <Input
                    id="firstName"
                    data-testid="operator-firstname-input"
                    value={form.firstName}
                    onChange={(e) => updateField("firstName", e.target.value)}
                    placeholder="Mario"
                    className={errors.firstName ? "border-red-500" : ""}
                    disabled={loading}
                  />
                  {errors.firstName && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {errors.firstName}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="lastName">Cognome *</Label>
                  <Input
                    id="lastName"
                    data-testid="operator-lastname-input"
                    value={form.lastName}
                    onChange={(e) => updateField("lastName", e.target.value)}
                    placeholder="Rossi"
                    className={errors.lastName ? "border-red-500" : ""}
                    disabled={loading}
                  />
                  {errors.lastName && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {errors.lastName}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="birthDate">Data di Nascita</Label>
                  <Input
                    id="birthDate"
                    type="date"
                    data-testid="operator-birthdate-input"
                    value={form.birthDate}
                    onChange={(e) => updateField("birthDate", e.target.value)}
                    disabled={loading}
                  />
                </div>
                <div>
                  <Label htmlFor="birthPlace">Luogo di Nascita</Label>
                  <Input
                    id="birthPlace"
                    data-testid="operator-birthplace-input"
                    value={form.birthPlace}
                    onChange={(e) => updateField("birthPlace", e.target.value)}
                    placeholder="Roma"
                    disabled={loading}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="fiscalCode">Codice Fiscale</Label>
                  <Input
                    id="fiscalCode"
                    data-testid="operator-fiscalcode-input"
                    value={form.fiscalCode}
                    onChange={(e) => updateField("fiscalCode", e.target.value)}
                    onBlur={() => handleBlur("fiscalCode")}
                    placeholder="RSSMRA80A01H501X"
                    maxLength={16}
                    className={cn("font-mono uppercase", errors.fiscalCode ? "border-red-500" : "")}
                    disabled={loading}
                  />
                  {errors.fiscalCode && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {errors.fiscalCode}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <Separator className="my-4" />

            {/* Residence Section */}
            <div className="space-y-4">
              <h4 className="font-medium text-sm text-slate-700 flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Residenza
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="residenceAddress">Indirizzo</Label>
                  <Input
                    id="residenceAddress"
                    data-testid="operator-address-input"
                    value={form.residenceAddress}
                    onChange={(e) => updateField("residenceAddress", e.target.value)}
                    placeholder="Via Roma, 123"
                    disabled={loading}
                  />
                </div>
                <div>
                  <Label htmlFor="residenceCity">Città</Label>
                  <Input
                    id="residenceCity"
                    data-testid="operator-city-input"
                    value={form.residenceCity}
                    onChange={(e) => updateField("residenceCity", e.target.value)}
                    placeholder="Civitavecchia"
                    disabled={loading}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="residenceProvince">Provincia</Label>
                    <Input
                      id="residenceProvince"
                      data-testid="operator-province-input"
                      value={form.residenceProvince}
                      onChange={(e) => updateField("residenceProvince", e.target.value.toUpperCase())}
                      placeholder="RM"
                      maxLength={2}
                      className="uppercase"
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <Label htmlFor="residenceCap">CAP</Label>
                    <Input
                      id="residenceCap"
                      data-testid="operator-cap-input"
                      value={form.residenceCap}
                      onChange={(e) => updateField("residenceCap", e.target.value.replace(/\D/g, ""))}
                      placeholder="00053"
                      maxLength={5}
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
            </div>

            <Separator className="my-4" />

            {/* Contact Section */}
            <div className="space-y-4">
              <h4 className="font-medium text-sm text-slate-700 flex items-center gap-2">
                <Phone className="h-4 w-4" />
                Contatti
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="operator-email-input"
                    value={form.email}
                    onChange={(e) => updateField("email", e.target.value)}
                    placeholder="mario.rossi@studio.it"
                    disabled={loading}
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Telefono</Label>
                  <Input
                    id="phone"
                    data-testid="operator-phone-input"
                    value={form.phone}
                    onChange={(e) => updateField("phone", e.target.value)}
                    placeholder="333 123 4567"
                    disabled={loading}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="username" className="font-semibold text-blue-700">Username (per accesso al sistema)</Label>
                  <Input
                    id="username"
                    data-testid="operator-username-input"
                    value={form.username}
                    onChange={(e) => updateField("username", e.target.value.toLowerCase().replace(/\s/g, ''))}
                    placeholder="Es. m.rossi"
                    disabled={loading}
                    className="font-mono"
                  />
                  <p className="text-xs text-slate-500 mt-1">L'operatore accederà al sistema con questo username e la password assegnata</p>
                </div>
              </div>
            </div>

            <Separator className="my-4" />

            {/* Identity Document Section */}
            <div className="space-y-4">
              <h4 className="font-medium text-sm text-slate-700 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Documento di Identità
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="documentType">Tipo Documento</Label>
                  <Select
                    value={form.documentType}
                    onValueChange={(value) => updateField("documentType", value)}
                  >
                    <SelectTrigger data-testid="operator-doctype-select">
                      <SelectValue placeholder="Seleziona tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {DOCUMENT_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="documentNumber">Numero Documento</Label>
                  <Input
                    id="documentNumber"
                    data-testid="operator-docnumber-input"
                    value={form.documentNumber}
                    onChange={(e) => updateField("documentNumber", e.target.value.toUpperCase())}
                    placeholder="AA1234567"
                    className="uppercase"
                    disabled={loading}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="documentIssuer">Ente Rilascio</Label>
                  <Input
                    id="documentIssuer"
                    data-testid="operator-docissuer-input"
                    value={form.documentIssuer}
                    onChange={(e) => updateField("documentIssuer", e.target.value)}
                    placeholder="Comune di Roma"
                    disabled={loading}
                  />
                </div>
                <div>
                  <Label htmlFor="documentIssueDate">Data Rilascio</Label>
                  <Input
                    id="documentIssueDate"
                    type="date"
                    data-testid="operator-docissuedate-input"
                    value={form.documentIssueDate}
                    onChange={(e) => updateField("documentIssueDate", e.target.value)}
                    disabled={loading}
                  />
                </div>
                <div>
                  <Label htmlFor="documentExpiryDate">Data Scadenza</Label>
                  <Input
                    id="documentExpiryDate"
                    type="date"
                    data-testid="operator-docexpirydate-input"
                    value={form.documentExpiryDate}
                    onChange={(e) => updateField("documentExpiryDate", e.target.value)}
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            <Separator className="my-4" />

            {/* v1.10.1 · 22/07/2026 — Sorveglianza sanitaria (D.Lgs 81/2008).
                Etichetta rinominata da "visita medica" a "sorveglianza sanitaria"
                (terminologia normativa corretta). Il campo backend resta
                `medicalVisitExpiryDate` per non spezzare i dati esistenti.
                Monitorata dallo scadenzario del cruscotto (pannello
                "Documenti clienti · Adempimenti") con soglia di preavviso
                del gestionale di default 90 giorni (configurabile in
                release successive). */}
            <div className="space-y-4">
              <h4 className="font-medium text-sm text-slate-700 flex items-center gap-2">
                <ShieldCheck className="h-4 w-4" style={{ color: "var(--gis-green)" }} />
                Sorveglianza sanitaria
                <span className="text-[10px] font-normal text-slate-500 gis-body">
                  · D.Lgs 81/2008 · dove applicabile
                </span>
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="medicalVisitExpiryDate">Data prossima visita di sorveglianza sanitaria</Label>
                  <Input
                    id="medicalVisitExpiryDate"
                    type="date"
                    data-testid="operator-medical-expiry-input"
                    value={form.medicalVisitExpiryDate || ""}
                    onChange={(e) => updateField("medicalVisitExpiryDate", e.target.value)}
                    disabled={loading}
                  />
                  <p className="text-[10px] text-slate-500 gis-body mt-1">
                    Soglia di preavviso del gestionale: 90 giorni prima della scadenza
                    (le visite hanno spesso liste d&apos;attesa da 30-60 giorni).
                    Compilare solo per gli operatori soggetti a sorveglianza sanitaria.
                  </p>
                </div>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={resetForm} disabled={loading}>
                Annulla
              </Button>
              <Button
                onClick={handleSave}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="save-operator-button"
                disabled={loading}
              >
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingOperator ? "Aggiorna" : "Salva Operatore"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Account Management Dialog */}
        <Dialog open={accountDialogOpen} onOpenChange={setAccountDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {accountDialogMode === "create" && (
                  <>
                    <Key className="h-5 w-5 text-green-600" />
                    Crea Account di Accesso
                  </>
                )}
                {accountDialogMode === "reset" && (
                  <>
                    <KeyRound className="h-5 w-5 text-amber-600" />
                    Reset Password
                  </>
                )}
                {accountDialogMode === "delete" && (
                  <>
                    <UserX className="h-5 w-5 text-red-600" />
                    Elimina Account
                  </>
                )}
              </DialogTitle>
              <DialogDescription>
                {selectedOperator && (
                  <span className="font-medium">{selectedOperator.fullName}</span>
                )}
                {accountDialogMode === "create" && selectedOperator && (
                  <span> - {selectedOperator.email}</span>
                )}
              </DialogDescription>
            </DialogHeader>

            {accountDialogMode === "delete" ? (
              <div className="py-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800">
                    Stai per eliminare l'account di accesso per questo operatore.
                    <br /><br />
                    <strong>L'operatore non potrà più accedere al sistema.</strong>
                    <br /><br />
                    Potrai sempre creare un nuovo account in seguito.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4 py-4">
                {accountDialogMode === "create" && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800">
                      L'operatore potrà accedere con:
                      <br />
                      <strong>Email:</strong> {selectedOperator?.email}
                      <br />
                      <strong>oppure Username</strong> (inserisci sotto)
                    </p>
                  </div>
                )}
                
                {accountDialogMode === "create" && (
                <div>
                  <Label htmlFor="username">Username (opzionale)</Label>
                  <Input
                    id="username"
                    type="text"
                    data-testid="account-username-input"
                    value={passwordForm.username || ""}
                    onChange={(e) => setPasswordForm({ ...passwordForm, username: e.target.value.toLowerCase().replace(/\s/g, '') })}
                    placeholder="Es. m.bianchi"
                    disabled={loading}
                  />
                  <p className="text-xs text-slate-500 mt-1">Se inserito, l'operatore potrà accedere anche con lo username</p>
                </div>
                )}
                
                <div>
                  <Label htmlFor="password">
                    {accountDialogMode === "create" ? "Password" : "Nuova Password"} *
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      data-testid="account-password-input"
                      value={passwordForm.password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, password: e.target.value })}
                      placeholder="Minimo 6 caratteri"
                      className={passwordErrors.password ? "border-red-500 pr-10" : "pr-10"}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {passwordErrors.password && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {passwordErrors.password}
                    </p>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="confirmPassword">Conferma Password *</Label>
                  <Input
                    id="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    data-testid="account-confirm-password-input"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    placeholder="Ripeti la password"
                    className={passwordErrors.confirmPassword ? "border-red-500" : ""}
                    disabled={loading}
                  />
                  {passwordErrors.confirmPassword && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {passwordErrors.confirmPassword}
                    </p>
                  )}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={closeAccountDialog} disabled={loading}>
                Annulla
              </Button>
              {accountDialogMode === "create" && (
                <Button
                  onClick={handleCreateAccount}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="confirm-create-account-button"
                  disabled={loading}
                >
                  {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Crea Account
                </Button>
              )}
              {accountDialogMode === "reset" && (
                <Button
                  onClick={handleResetPassword}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="confirm-reset-password-button"
                  disabled={loading}
                >
                  {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Aggiorna Password
                </Button>
              )}
              {accountDialogMode === "delete" && (
                <Button
                  onClick={handleDeleteAccount}
                  variant="destructive"
                  data-testid="confirm-delete-account-button"
                  disabled={loading}
                >
                  {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Elimina Account
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}

